import { GAME_MODES } from '@blind-kings/game-core';
import { api } from 'aws-blocks';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import { DEFAULT_BOT_DIFFICULTY } from '../bot/difficulty';
import { useAuthContext } from '../contexts/AuthContext';
import { useSnackBarContext } from '../contexts/SnackBarContext';
import useApiAction from '../hooks/use-api-action';
import { resolveOnlineGameId } from '../utils/active-games';

export type GameModeModalProps = {
  show: boolean;
  onClose: () => void;
};

type ModeCardProps = {
  eyebrow: string;
  title: string;
  description: string;
  accent: 'gold' | 'accent' | 'danger' | 'muted';
  disabled?: boolean;
  badge?: string;
  onClick: () => void;
};

const accentRing: Record<ModeCardProps['accent'], string> = {
  gold: 'border-bk-gold/70 hover:border-bk-gold-bright hover:shadow-[0_0_28px_color-mix(in_srgb,var(--color-bk-gold)_35%,transparent)]',
  accent:
    'border-bk-accent/50 hover:border-bk-accent hover:shadow-[0_0_24px_color-mix(in_srgb,var(--color-bk-accent)_30%,transparent)]',
  danger:
    'border-bk-danger/50 hover:border-bk-danger-bright hover:shadow-[0_0_24px_color-mix(in_srgb,var(--color-bk-danger)_28%,transparent)]',
  muted: 'border-bk-border hover:border-bk-gold-dim/60',
};

const accentLabel: Record<ModeCardProps['accent'], string> = {
  gold: 'text-bk-gold-bright',
  accent: 'text-sky-300',
  danger: 'text-bk-danger-bright',
  muted: 'text-bk-muted',
};

function ModeCard({
  eyebrow,
  title,
  description,
  accent,
  disabled,
  badge,
  onClick,
}: ModeCardProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        'group relative flex min-h-[8rem] flex-col items-start justify-between overflow-hidden border bg-bk-bg/80 p-3 text-left transition-all duration-200 sm:min-h-[9rem] sm:p-4',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-bk-gold focus-visible:ring-offset-2 focus-visible:ring-offset-bk-panel',
        'disabled:pointer-events-none disabled:opacity-45',
        accentRing[accent],
      ].join(' ')}
    >
      <div
        className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-30 blur-2xl transition-opacity group-hover:opacity-55"
        style={{
          background:
            accent === 'gold'
              ? 'var(--color-bk-gold)'
              : accent === 'accent'
                ? 'var(--color-bk-accent)'
                : accent === 'danger'
                  ? 'var(--color-bk-danger)'
                  : 'var(--color-bk-border)',
        }}
      />
      <div className="relative w-full">
        <div className="flex items-start justify-between gap-2">
          <p
            className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${accentLabel[accent]}`}
          >
            {eyebrow}
          </p>
          {badge ? (
            <span className="shrink-0 border border-bk-gold-dim/50 bg-bk-panel-raised px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-bk-gold">
              {badge}
            </span>
          ) : null}
        </div>
        <h3 className="mt-1 text-lg font-extrabold uppercase tracking-wider text-bk-text sm:text-xl">
          {title}
        </h3>
      </div>
      <p className="relative mt-2 text-[11px] leading-snug text-bk-muted sm:mt-3 sm:text-sm">
        {description}
      </p>
    </button>
  );
}

export default function GameModeModal({ show, onClose }: GameModeModalProps) {
  const navigate = useNavigate();
  const { addSnackBar } = useSnackBarContext();
  const { currentUser, userData, openLoginModal } = useAuthContext();
  const [createLocalGame, loadingLocalGame] = useApiAction({
    action: async () => api.createGame(GAME_MODES.LOCAL_GAME),
    reloadUserData: true,
  });
  const [createBotGame, loadingBotGame] = useApiAction({
    action: async () =>
      api.createGame(GAME_MODES.AI_GAME, {
        botDifficulty: DEFAULT_BOT_DIFFICULTY,
      }),
    reloadUserData: true,
  });

  const botGameId = userData?.currentBotGameId;
  const localGameId = userData?.currentLocalGameId;
  const onlineGameId = resolveOnlineGameId(userData);
  const onlineMode = userData?.currentOnlineGameMode as string | undefined;

  const onlineIsRanked = onlineMode === GAME_MODES.RANKED_GAME;
  const onlineIsOnline =
    !onlineGameId ||
    onlineMode === GAME_MODES.ONLINE_GAME ||
    (!onlineMode && !!onlineGameId);

  function go(path: string) {
    onClose();
    navigate(path);
  }

  function requireAuthThen(path: string) {
    if (!currentUser) {
      onClose();
      openLoginModal();
      return;
    }
    go(path);
  }

  async function handleLocalGameClick() {
    if (localGameId) {
      go(`/play/${localGameId}`);
      return;
    }
    if (!currentUser) {
      onClose();
      openLoginModal();
      return;
    }
    try {
      const createGameData = (await createLocalGame()) as { gameId?: string } | undefined;
      const gameId = createGameData?.gameId;
      if (gameId) go(`/play/${gameId}`);
    } catch (err) {
      console.error(err);
      addSnackBar({
        title: 'Error Making Game',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 2500,
      });
    }
  }

  function handleOnlineClick() {
    if (onlineGameId) {
      go(`/play/${onlineGameId}`);
      return;
    }
    requireAuthThen(`/queue/${GAME_MODES.ONLINE_GAME}`);
  }

  function handleRankedClick() {
    if (onlineGameId) {
      go(`/play/${onlineGameId}`);
      return;
    }
    requireAuthThen(`/queue/${GAME_MODES.RANKED_GAME}`);
  }

  async function handleStartBot() {
    if (botGameId) {
      go(`/play/${botGameId}`);
      return;
    }
    if (!currentUser) {
      onClose();
      openLoginModal();
      return;
    }
    try {
      const createGameData = (await createBotGame()) as
        | { gameId?: string }
        | undefined;
      const gameId = createGameData?.gameId;
      if (gameId) go(`/play/${gameId}`);
    } catch (err) {
      console.error(err);
      addSnackBar({
        title: 'Error Making Game',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 2500,
      });
    }
  }

  return (
    <>
      <Modal show={loadingLocalGame || loadingBotGame}>
        <div className="text-xl animate-bk-pulse-soft text-bk-gold uppercase tracking-wider">
          {loadingBotGame ? 'Creating Bot Game' : 'Creating Local Game'}
        </div>
      </Modal>
      <Modal
        show={show && !loadingLocalGame && !loadingBotGame}
        onClose={onClose}
        title="Enter the Arena"
        titleClassName="text-2xl font-extrabold uppercase tracking-[0.2em] text-bk-gold-bright sm:text-3xl"
      >
        <div className="space-y-4 text-left">
          <p className="text-center text-sm text-bk-muted">
            Choose how you want to clash — every mode uses the same ruthless board.
          </p>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <ModeCard
              accent="gold"
              eyebrow="Quick match"
              title={onlineGameId && onlineIsOnline ? 'Continue Online' : 'Online'}
              description={
                onlineGameId && !onlineIsOnline
                  ? 'Finish your ranked match before queuing online.'
                  : 'Find a rival instantly. Timed turns. Full rewards.'
              }
              badge={onlineGameId && onlineIsOnline ? 'Live' : undefined}
              disabled={!!onlineGameId && !onlineIsOnline}
              onClick={handleOnlineClick}
            />
            <ModeCard
              accent="danger"
              eyebrow="High stakes"
              title={onlineGameId && onlineIsRanked ? 'Continue Ranked' : 'Ranked'}
              description={
                onlineGameId && !onlineIsRanked
                  ? 'Finish your online match before queuing ranked.'
                  : 'Separate queue. 15% reward boost. Prove your crown.'
              }
              badge={onlineGameId && onlineIsRanked ? 'Live' : undefined}
              disabled={!!onlineGameId && !onlineIsRanked}
              onClick={handleRankedClick}
            />
            <ModeCard
              accent="accent"
              eyebrow="Practice"
              title={localGameId ? 'Continue Local' : 'Local'}
              description="Hot-seat both armies on one device. Perfect for teaching the rules."
              badge={localGameId ? 'Resume' : undefined}
              onClick={() => void handleLocalGameClick()}
            />
            <ModeCard
              accent="accent"
              eyebrow="Solo"
              title={
                loadingBotGame
                  ? 'Creating…'
                  : botGameId
                    ? 'Continue Bot'
                    : 'Bot'
              }
              description={
                botGameId
                  ? 'Return to your match against Blind Bot.'
                  : 'Play Blind Bot at full strength.'
              }
              badge={botGameId ? 'Resume' : undefined}
              disabled={loadingBotGame}
              onClick={() => void handleStartBot()}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
